// import { Network, ConnectionStatus } from '@capacitor/network';
import type { Dayjs } from 'dayjs'
import dayJS from 'dayjs'
import { defineStore } from 'pinia'
import { Dark, Platform } from 'quasar'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { useServerSettingsStore } from 'src/CrudManager/Core/stores/useServerSettingsStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { capitalize, ref } from 'vue'
import { useAppSettingsStore } from './useAppSettingsStore'

// Define an interface for the Company data expected from the backend
interface CompanyData {
  id: string // Assuming UUID
  name: string
  badge_label: string | null
  badge_color: string | null
  is_default: boolean
  is_active: boolean
}

// Define the expected structure of the syncAppData response
interface SyncAppDataResponse {
  // serverSettings: Record<string, any>; // Assuming structure if needed, removed for now
  companies: CompanyData[]
  diff: number
}

// Network types
interface ConnectionStatus {
  connected: boolean
  connectionType: string
}

export const useGlobalStore = defineStore(
  'global-store',
  () => {
    let serverTimeDiff = 0
    const virtualDate = ref(null as string | null)
    const connectionStatus = ref(null as ConnectionStatus | null)
    const pageTitle = ref(import.meta.env.APP_NAME)

    const isDarkMode = ref(false)

    // Initialize dark mode from localStorage or system preference
    const initDarkMode = () => {
      const savedDarkMode = localStorage.getItem('darkMode')
      if (savedDarkMode !== null) {
        isDarkMode.value = savedDarkMode === 'true'
      } else {
        // Check system preference
        isDarkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
      }
      Dark.set(isDarkMode.value)
    }

    const toggleDarkMode = (value?: boolean) => {
      // If a value is provided, use it; otherwise toggle the current value
      isDarkMode.value = value !== undefined ? value : !isDarkMode.value
      Dark.set(isDarkMode.value)
      localStorage.setItem('darkMode', isDarkMode.value.toString())
    }

    const syncAppData = async (): Promise<void> => {
      const userStore = useUserStore()
      const settingsStore = useServerSettingsStore()

      const uid = userStore.user && userStore.user.id ? `/${userStore.user.id}` : ''
      try {
        const { data } = await axiosApi.post<SyncAppDataResponse>(`syncAppData${uid}`, {
          app_version: import.meta.env.BUILD_DATE,
          url: `${capitalize(Platform.is.name)} ${Platform.is.versionNumber}`,

          datetime: dayJS().unix(),
        })
        serverTimeDiff = data.diff

        // Store companies in AppSettingsStore
        const appSettingsStore = useAppSettingsStore()
        appSettingsStore.setAvailableCompanies(data.companies) // Call a new setter function

        // set settings after syncing app data
        settingsStore.settings = data.server_settings

        dayJS.updateLocale('fr-ca', {
          weekStart: settingsStore.getSetting('first_day_of_week').value,
        })
      } catch (err) {
        console.error('Error syncing app data:', err)
        // showErrorNotification(err);
      }
    }

    const updateVirtualDate = async (): Promise<void> => {
      const userStore = useUserStore()
      const time = userStore.user?.day_switch_time ?? '06:00:00'
      const date = dayJS().isBefore(`${dayJS().format('YYYY-MM-DD')} ${time}`) ? dayJS().subtract(1, 'day') : dayJS()
      virtualDate.value = date.format('YYYY-MM-DD')
    }

    const setConnectionStatus = async (): Promise<void> => {
      try {
        // Commented out until @capacitor/network is properly imported
        // connectionStatus.value = await Network.getStatus();
        connectionStatus.value = {
          connected: navigator.onLine,
          connectionType: navigator.onLine ? 'unknown' : 'none',
        }
      } catch (err) {
        console.error('Error getting connection status:', err)
      }
    }

    const getCurrentTime = async (): Promise<Dayjs> => {
      let currTime: Dayjs
      if (serverTimeDiff <= 0) {
        currTime = dayJS().subtract(Math.abs(serverTimeDiff), 's')
      } else {
        currTime = dayJS().add(serverTimeDiff, 's')
      }

      return currTime
    }

    const appVersion = import.meta.env.VITE_BUILD_VERSION || `dev.${import.meta.env.VITE_BUILD_NUMBER || '0'}`
    const buildDate = import.meta.env.VITE_BUILD_DATE || dayJS().format('YYYY-MM-DD HH:mm:ss')

    const resetApp = async (): Promise<void> => {
      // empty local storage
      localStorage.clear()
      // delete all data from IndexedDB
      const dbs = await window.indexedDB.databases()
      dbs.forEach((db) => {
        window.indexedDB.deleteDatabase(db.name)
      })
      // reset user store
    }
    return {
      resetApp,
      serverTimeDiff,
      virtualDate,
      connectionStatus,
      pageTitle,

      syncAppData,
      updateVirtualDate,
      setConnectionStatus,
      getCurrentTime,
      appVersion,
      buildDate,
      isDarkMode,
      toggleDarkMode,
      initDarkMode,
    }
  },

  { persist: true },
)
